import React, { useCallback, useMemo } from 'react';
import FormEdit from '../../../../core/components/form/FormEdit';
import ValidInputDroplistText from '../../../../core/components/validation/ValidInputDroplistText';
import ValidCheckbox from '../../../../core/components/validation/ValidCheckbox';
import Button from '../../../../core/components/Button';
import FormSearch from '../../../../core/components/FormSearch';
import DateRangeController from '../../../../core/components/DateRangeController';
import { useAtomValue } from 'jotai';
import { splitOrderInvoicesPageActionAtom } from '../split_order_invoices_list_atoms';
import { useSplitOrderInvoicesSearchState } from './useSplitOrderInvoicesSearchState';
import { useSplitOrderInvoicesSearchInit } from './useSplitOrderInvoicesSearchInit';
import { useSplitOrderInvoicesStoreActions } from '../pagehooks/useSplitOrderInvoicesStore';
import { useDateRangeControllerState } from '../../../../core/hooks/useDateRangeControllerState';
import { SPLIT_ORDER_INVOICES_DATE_RANGE_MODES } from '../../../../core/utils/dateRangeModesByFeature.const';
import { parseLocalDate } from '../../../../core/components/validation/ValidInputDate.utils';

const SplitOrderInvoicesListSearch: React.FC = () => {
    const action = useAtomValue(splitOrderInvoicesPageActionAtom);
    const init = useSplitOrderInvoicesSearchInit();
    const drawerOpen = action === 'QUERY' || init;
    return drawerOpen ? <SplitOrderInvoicesListForm disabled={init} /> : <SplitOrderInvoicesListFormSearch />;
};

type FormProps = {
    disabled?: boolean;
};

const SplitOrderInvoicesListForm = ({ disabled }: FormProps): JSX.Element => {
    const {
        state,
        handleRxChange,
        handleCeChange,
        toggleRebatesOnly,
        handleSearch,
        setProperty,
        setMultipleProperties,
    } = useSplitOrderInvoicesSearchState();

    const dateRangeMode = typeof state.DateRangeMode?.val === 'string' ? state.DateRangeMode.val : 'MTD';
    const dateRangeStart = state.StartDate?.val ? parseLocalDate(state.StartDate.val) : new Date();
    const dateRangeEnd = state.EndDate?.val ? parseLocalDate(state.EndDate.val) : new Date();

    const setPropertyWrapper = useCallback(
        (key: string, val: any) => setProperty(key as keyof typeof state, val),
        [setProperty],
    );

    const setMultiplePropertiesWrapper = useCallback(
        (updates: Record<string, any>) => setMultipleProperties(updates as any),
        [setMultipleProperties],
    );

    const {
        monthlyMonth,
        monthlyYear,
        qtr,
        qtrYear,
        handleModeChange,
        handleStartChange,
        handleEndChange,
        handleMonthlyChange,
        handleQuarterChange,
        startError,
        endError,
    } = useDateRangeControllerState(
        dateRangeMode,
        dateRangeStart,
        dateRangeEnd,
        setPropertyWrapper,
        setMultiplePropertiesWrapper,
    );

    const handleStartChangeWithError = useCallback(
        (date: Date, setLocalError?: (msg: string) => void): void => {
            handleStartChange(date);
            if (setLocalError) setLocalError('');
        },
        [handleStartChange],
    );

    const handleEndChangeWithError = useCallback(
        (date: Date, setLocalError?: (msg: string) => void): void => {
            handleEndChange(date);
            if (setLocalError) setLocalError('');
        },
        [handleEndChange],
    );

    const showCe = state.CE?.options?.length > 0;

    return (
        <FormEdit
            formName="splitOrderInvoicesSearch"
            cssName="split-order-invoices-search"
            fieldsetId="split-order-invoices-search-fieldset"
        >
            <div className="fieldset-form">
                <div className="fieldset-inline">
                    <div className="fieldset-section">
                        <ValidInputDroplistText
                            prop={state.RX}
                            onChange={(_key, val) => handleRxChange(String(val))}
                        />
                        {showCe && (
                            <ValidInputDroplistText
                                prop={state.CE}
                                onChange={(_key, val) => handleCeChange(String(val))}
                                noMinWidth
                            />
                        )}
                    </div>
                </div>
                <div className="fieldset-inline">
                    <DateRangeController
                        allowedModes={SPLIT_ORDER_INVOICES_DATE_RANGE_MODES}
                        mode={dateRangeMode}
                        start={dateRangeStart}
                        end={dateRangeEnd}
                        monthlyMonth={monthlyMonth}
                        monthlyYear={monthlyYear}
                        qtr={qtr}
                        qtrYear={qtrYear}
                        onModeChange={handleModeChange}
                        onStartChange={handleStartChangeWithError}
                        onEndChange={handleEndChangeWithError}
                        onMonthlyChange={handleMonthlyChange}
                        onQuarterChange={handleQuarterChange}
                        startError={startError}
                        endError={endError}
                        disabled={disabled}
                    />
                </div>
                <div className="form-field">
                    <ValidCheckbox
                        id="rebates-only"
                        label="Rebates Only"
                        checked={state.RebatesOnly}
                        onChange={toggleRebatesOnly}
                    />
                </div>
                <div className="form-field">
                    <Button
                        onClick={handleSearch}
                        cssName="btn-submit"
                        disabled={disabled}
                    >
                        Search
                    </Button>
                </div>
            </div>
        </FormEdit>
    );
};

const SplitOrderInvoicesListFormSearch = (): JSX.Element => {
    const { setAction } = useSplitOrderInvoicesStoreActions();
    const { state } = useSplitOrderInvoicesSearchState();

    const handleEdit = useCallback(() => {
        setTimeout(() => setAction('QUERY'), 500);
    }, [setAction]);

    // Show the pharmacy's dropdown label (e.g. "LLUCH CHRX") in the search
    // criteria summary instead of its raw code value.
    const criteria = useMemo(() => {
        const rxLabel = state.RX?.options?.find(opt => String(opt.val) === String(state.RX?.val))?.lbl;
        if (!rxLabel) return state;
        return { ...state, RX: { ...state.RX, val: rxLabel } };
    }, [state]);

    return (
        <FormSearch
            criteria={criteria}
            onEdit={handleEdit}
        />
    );
};

export default SplitOrderInvoicesListSearch;
